import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { DOMAINES } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", subject: "", message: "", consent: false, website: "" };

const Field = ({ label, id, children }) => (
  <label htmlFor={id} className="block">
    <span className="eyebrow text-[#4A5552]">{label}</span>
    {children}
  </label>
);

const inputCls = "mt-2 w-full border-0 border-b border-[#3D5A4C]/25 bg-transparent px-0 py-3 text-[1.02rem] text-[#1C2826] placeholder:text-[#8a948f] outline-none transition-colors focus:border-[#3D5A4C]";

export const ContactForm = () => {
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) return toast.error("Merci d'accepter le traitement de vos données pour envoyer votre message.");
    setSending(true);
    try {
      await axios.post(`${API}/contact`, f);
      toast.success("Votre message a bien été transmis. Le cabinet vous recontactera dans les meilleurs délais.");
      setF(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? "Merci de vérifier les champs du formulaire (message de 10 caractères minimum)." : "L'envoi a échoué. Vous pouvez joindre le cabinet au 04 42 38 25 84.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="grid gap-8 sm:grid-cols-2">
      <Field label="Nom et prénom *" id="c-name">
        <input id="c-name" data-testid="contact-name-input" required minLength={2} value={f.name} onChange={set("name")} className={inputCls} autoComplete="name" />
      </Field>
      <Field label="E-mail *" id="c-email">
        <input id="c-email" type="email" data-testid="contact-email-input" required value={f.email} onChange={set("email")} className={inputCls} autoComplete="email" />
      </Field>
      <Field label="Téléphone" id="c-phone">
        <input id="c-phone" type="tel" data-testid="contact-phone-input" value={f.phone} onChange={set("phone")} className={inputCls} autoComplete="tel" />
      </Field>
      <Field label="Objet *" id="c-subject">
        <select id="c-subject" data-testid="contact-subject-select" required value={f.subject} onChange={set("subject")} className={`${inputCls} cursor-pointer`}>
          <option value="" disabled>Choisir…</option>
          {DOMAINES.map((d) => <option key={d.k} value={d.t}>{d.t}</option>)}
          <option value="Autre demande">Autre demande</option>
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Votre message *" id="c-message">
          <textarea id="c-message" data-testid="contact-message-input" required minLength={10} rows={5} value={f.message} onChange={set("message")}
            placeholder="Décrivez brièvement votre situation (date et nature de l'accident…)" className={`${inputCls} resize-none`} />
        </Field>
      </div>
      <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
      <div className="flex items-start gap-3 sm:col-span-2">
        <Checkbox id="c-consent" data-testid="contact-consent-checkbox" checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: v === true })} className="mt-1 rounded-none border-[#3D5A4C]" />
        <label htmlFor="c-consent" className="text-[0.85rem] leading-relaxed text-[#4A5552]">
          J'accepte que les informations saisies soient utilisées par le cabinet pour répondre à ma demande. Elles sont couvertes par le secret professionnel
          et ne sont jamais cédées à des tiers. <Link to="/confidentialite" data-testid="contact-privacy-link" className="text-[#3D5A4C] underline underline-offset-2">Politique de confidentialité</Link>.
        </label>
      </div>
      <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-[#5b6763]">* Champs obligatoires. L'envoi de ce formulaire ne vaut pas acceptation du dossier.</p>
        <button type="submit" disabled={sending} data-testid="contact-submit-button"
          className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#3D5A4C] py-4 pl-7 pr-5 text-[0.95rem] font-medium text-[#FAF8F5] transition-colors duration-300 hover:bg-[#2E453A] disabled:opacity-60">
          {sending ? "Envoi en cours" : "Envoyer ma demande"}
          <span className="grid h-8 w-8 place-items-center rounded-full bg-[#FAF8F5]/15 transition-transform duration-500 group-hover:translate-x-1">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} strokeWidth={1.5} />}
          </span>
        </button>
      </div>
    </form>
  );
};
